/**
 * bereavement-tone — the staff-side copy register (#685).
 *
 * Every string a TRFer sees about a bereaved family lives in this file, and
 * so does its treatment-era counterpart, so TRF's tone review is a one-file
 * edit and the two registers can be read side by side.
 *
 * ── What this module is actually for ──────────────────────────────────────
 *
 * The defect #685 reports is not "the words are too harsh". It is that
 * `silentCount` measures the wrong thing for a bereaved family. The metric
 * exists to predict families dropping out of support *during treatment*, and
 * its remedy is a nudge. Neither premise holds after a child dies: silence
 * may be the expected and correct state, and there is nothing to nudge them
 * back into. Rewording the sentence attached to a metric that is measuring
 * the wrong thing still tells the wrong story — and it permanently inflates
 * "Silent · 7+ days", so a stat standing at four months trains counselors to
 * ignore that stat for *everyone*.
 *
 * So the register switch is paired with an exclusion at source
 * (`countsTowardSilence`), and the exclusion is paired with a replacement
 * (`buildRemembranceCallouts`). Dropping bereaved families out of the count
 * without giving them their own proactive surface takes the platform from
 * tone-deaf to mute, and contact falls to zero for exactly the families where
 * an unprompted note matters most.
 *
 * ── Register discipline ───────────────────────────────────────────────────
 *
 * Operational, never condolence. The bereaved register is factual,
 * direction-flipped (it measures when *we* last reached out, not when *they*
 * last engaged) and calendar-driven (remembrance dates and birthdays are the
 * outreach trigger, not a day counter). Condolence copy on a work surface
 * patronizes a professional. Over-softening ("held gently in this season of
 * loss") is a failure mode, not a goal — and it is the version that would
 * need a grief timer. Operational copy does not curdle at year three, which
 * is exactly why this keys on a single flag with no time thresholds.
 *
 * The one place the register is allowed warmth is the outbound draft in
 * `getCheckInDraft`, because that string is read by the parent, not by staff.
 *
 * ── DRAFT COPY — PENDING TRF TONE REVIEW (#685) ───────────────────────────
 * TRF owns the final wording of every string below. They ship ahead of that
 * review by explicit decision, in the same posture #644 took for the parent
 * memorial hero (docs/DECISIONS.md 2026-08-11): an empty suppression is also
 * a tone, and leaving "Silent · 4 mo" on a bereaved family's card was the
 * worse draft. The review request, with this exact copy, is the staff-side
 * section of docs/superpowers/memos/2026-08-11-memorial-copy-review.md.
 * When TRF answers, update the strings here and remove this banner.
 */
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { formatPreset } from '@/i18n/intl';

// ── The predicate ─────────────────────────────────────────────────────────

export type StaffRegister = 'treatment' | 'bereaved';

/**
 * Maps a family's treatment_phase to the staff-facing copy register.
 *
 * Deliberately the same shape as `resolveDashboardMode` (lib/dashboard-mode.ts),
 * which is the parent-side mapping — one switch on the same field, so the two
 * surfaces cannot drift into disagreeing about who is bereaved.
 *
 * Unknown / missing phases default to 'treatment' on purpose, mirroring that
 * function's fail-safe: we must never accidentally memorialize a living
 * patient. The cost of the two errors is not symmetric. Falling back to the
 * treatment register on a bereaved family shows a counselor a stat that reads
 * wrong; falling back to the bereaved register on a living one puts
 * remembrance framing on a child who is still in treatment. Only the first is
 * recoverable by the human reading the screen.
 *
 * Cross-reference: dashboard-mode.ts maps the same values to parent-facing
 * render modes, and lifecycle.ts maps them to staff lifecycle buckets. If the
 * phase-to-bucket assignment changes in one, change it in all three.
 */
export function resolveStaffRegister(treatmentPhase?: string | null): StaffRegister {
  switch (treatmentPhase) {
    case 'bereaved':
      return 'bereaved';
    default:
      return 'treatment';
  }
}

/**
 * The minimum a family record needs for the register to be resolved.
 * Structural on purpose: the caseload list holds `TRFerFamilyShape`, the
 * profile holds the raw `GET /trfer/families/:id` document, and both must
 * resolve to the same register.
 */
export interface RegisterFamilyShape {
  patient?: { treatment_phase?: string | null } | null;
}

export function familyStaffRegister(family: RegisterFamilyShape | null | undefined): StaffRegister {
  return resolveStaffRegister(family?.patient?.treatment_phase);
}

export function isBereavedFamily(family: RegisterFamilyShape | null | undefined): boolean {
  return familyStaffRegister(family) === 'bereaved';
}

/**
 * The metric gate. A family counts toward the treatment-era engagement
 * signals only in the treatment register. It used to gate the silent count,
 * the silent bucket and the day counter as well; those retired with the dead
 * activity clock (#1983, 2026-09-15), and what is left behind the gate is the
 * engagement half of the attention score. Kept as its own predicate so the
 * exclusion cannot be applied on one surface and forgotten on another.
 */
export function countsTowardSilence(family: RegisterFamilyShape | null | undefined): boolean {
  return familyStaffRegister(family) === 'treatment';
}

/**
 * The *other* metric gate, and the one the first pass at #685 missed.
 *
 * `countsTowardSilence` covered the day counter. It did not cover
 * `health_metrics.risk_level`, and the two were the same measurement:
 * `buildFamilyHealthMetrics` (backend/src/routes/trfer.js) took the MAX of an
 * engagement rank and an inactivity rank, and engagement itself is tasks
 * created and completed over the last 30 days. Both axes were "did this family
 * do things lately". (The inactivity axis has since retired outright — #1983 —
 * and `risk_level` stands on engagement alone, which is still that same
 * question.) `risk_driver` cannot rescue the distinction either: its one
 * surviving value, 'engagement', is that axis, so no driver value means
 * "something real happened to this family".
 *
 * The consequence, which is the bug this gate exists to close: excluding only
 * the inactivity axis let the engagement axis walk a bereaved family straight
 * back to `risk_level: 'critical'` with `risk_driver: 'mixed'`, which rendered
 * a rose CRITICAL pill, printed "Active escalation" on the signal line, filed
 * the family under Escalated and pinned it to the top of the caseload. The
 * word "Silent" had been removed and replaced with a worse one.
 *
 * So in the bereaved register the derived tier is not a fact about the family
 * and no surface may render it. Nothing real is hidden by this: escalations,
 * queue items, open tasks and remembrance dates are separate data and are all
 * untouched — `risk_level` reads none of them.
 */
export function countsTowardRisk(family: RegisterFamilyShape | null | undefined): boolean {
  return familyStaffRegister(family) === 'treatment';
}

// ── Dates, formatted locally ──────────────────────────────────────────────
//
// This module does not import from trfer-display: trfer-display imports from
// here, and a second edge in the other direction would make the copy module
// depend on the page-token module it is supposed to be reviewable apart from.

/** A `last_check_in` record — structurally `FamilyCheckIn` from types/api. */
export interface CheckInLike {
  at: string;
  by_user_id?: string | null;
  by_name?: string | null;
}

function parsed(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  let d: Date;
  try { d = parseISO(iso); } catch { return null; }
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * "Jun 24" this year, "Jun 24, 2024" in any other year, or null when there is
 * no usable date.
 *
 * The year is not decoration. This helper feeds the "Last contact" stat value,
 * and the card renders that value with no note beside it — so a bare "Jun 24"
 * on a check-in from two years ago read as seven weeks ago on the one axis
 * this whole register exists to make honest. Only the ambiguous case pays the
 * extra characters; a contact from this year still reads "Jun 24".
 */
function shortDate(iso: string | null | undefined, now: Date = new Date()): string | null {
  const d = parsed(iso);
  if (!d) return null;
  // #2109 R3 L1: Intl in the UI locale ("Jun 24" / "24 jun"), not date-fns English.
  return formatPreset(d, d.getFullYear() === now.getFullYear() ? 'monthDay' : 'monthDayYear');
}

/**
 * The same short form, for a value that is a CALENDAR DATE rather than an
 * instant — `patient.angel_date`.
 *
 * `shortDate` is right for `last_check_in.at`, which is a real moment in time
 * and should be read in the viewer's zone. A remembrance date is not: staff
 * pick it from a `<input type="date">` and it is persisted as midnight UTC, so
 * formatting it in any US zone renders the day BEFORE. The profile page reads the
 * same field back with `.slice(0, 10)` — the raw UTC calendar day — so the
 * date field and the Remembrance stat cell sat one screen apart stating two
 * different days for the one date this register is organized around.
 *
 * Reading the calendar parts out of the string keeps both halves on the day
 * that was entered, in every zone.
 */
function shortCalendarDate(iso: string | null | undefined, now: Date): string | null {
  if (!iso) return null;
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  // Not a date-shaped string (a bare timestamp, say) — fall back rather than
  // drop the date entirely.
  if (!parts) return shortDate(iso, now);
  const local = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  if (Number.isNaN(local.getTime())) return null;
  return formatPreset(local, local.getFullYear() === now.getFullYear() ? 'monthDay' : 'monthDayYear');
}

/** "today" / "yesterday" / "6 days ago" / "3 wks ago" / "4 mo ago". */
function agoPhrase(iso: string | null | undefined, now: Date): string | null {
  const d = parsed(iso);
  if (!d) return null;
  const days = differenceInCalendarDays(now, d);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} wks ago`;
  return `${Math.floor(days / 30)} mo ago`;
}

// ── Stat cell: "Last contact" (bereaved register only) ────────────────────

/** One cell of the tone strip: mono label, serif value, one-line note. */
export interface StatCellCopy {
  label: string;
  value: string;
  note: string;
}

export interface LastContactStatInput {
  /** Staff outreach (#684). This is TRF → family — a real clock, written by
   *  the check-in route. */
  checkIn?: CheckInLike | null;
  now?: Date;
}

/**
 * The bereaved register's contact cell, used by the family card and the
 * family profile tone strip.
 *
 * This cell used to switch register: "Days silent" during treatment — a day
 * counter of *their* activity — and "Last contact" after. The treatment half
 * retired on 2026-09-15 (#1983): its day count read `Family.last_activity`,
 * which the backend stamped at intake and never advanced, so it was the
 * family's age wearing a judgement word. Under strict D-7 a figure nothing
 * measures is removed, not relabelled, and the treatment strip now simply has
 * no contact cell.
 *
 * What survives is the half that was always real: when TRF last reached out,
 * read from `last_check_in`. It is the register's organizing question (#685 —
 * direction-flipped, calendar-driven), which is why it stays a stat cell here
 * rather than folding into the sent-note beside the check-in button.
 *
 * A family with no recorded outreach degrades to "Not yet" — deliberately not
 * a number, and deliberately not a dash. There is no sentinel date to fall
 * back on and a false zero would read as "contacted today".
 */
export function getLastContactStat(input: LastContactStatInput): StatCellCopy {
  const now = input.now ?? new Date();
  const when = shortDate(input.checkIn?.at, now);
  const ago = agoPhrase(input.checkIn?.at, now);
  if (!when || !ago) {
    return {
      label: 'Last contact',
      value: 'Not yet',
      note: 'No outreach on record',
    };
  }
  return {
    label: 'Last contact',
    value: when,
    note: `We reached out ${ago}`,
  };
}

// ── Stat cell: "Engagement" → "Remembrance" ───────────────────────────────

export interface PrimaryStatInput {
  /** `health_metrics.engagement_score`, already ×100 and rounded. */
  engagementPct: number;
  /** `health_metrics.trends.engagement`. */
  trend?: 'up' | 'down' | 'stable' | null;
  /** `patient.angel_date` — the remembrance date (#633). */
  angelDate?: string | null;
  now?: Date;
}

/**
 * The tone strip's *first* cell, on both the family card and the profile.
 *
 * This is the neighbour that did not switch when the middle cell did, and the
 * pair produced the sentence that named this defect: "Engagement · 0% ·
 * Trending downward" sitting beside "Last contact · Not yet · No outreach on
 * record". One screen saying TRF has never reached out and, in the same
 * breath, that the family's participation is declining.
 *
 * It is not a near-miss either — it is guaranteed. `trends.engagement` is set
 * to 'down' whenever the engagement score drops below 0.3, and a bereaved
 * family's score decays past that within weeks of the last task closing. Every
 * bereaved family reaches "Trending downward" and stays there.
 *
 * So the bereaved register does not soften the judgment, it removes the metric
 * and puts the register's own organizing fact in the slot: the remembrance
 * date, which is what actually drives outreach here (#685 — calendar-driven,
 * not day-counter-driven).
 */
export function getPrimaryStat(
  register: StaffRegister,
  input: PrimaryStatInput,
): StatCellCopy {
  if (register === 'bereaved') {
    const when = shortCalendarDate(input.angelDate, input.now ?? new Date());
    return when
      ? { label: 'Remembrance', value: when, note: 'Held on the team calendar' }
      : { label: 'Remembrance', value: 'Not set', note: 'No date on this record yet' };
  }

  return {
    label: 'Engagement',
    value: `${input.engagementPct}%`,
    note:
      input.trend === 'up' ? 'Trending upward'
        : input.trend === 'down' ? 'Trending downward'
          : 'Steady',
  };
}

// ── Stat cell: open items ─────────────────────────────────────────────────

export interface OpenItemsStatInput {
  openCount: number;
  /** `tasksNeedingReconcile(open_tasks).length` — 0 in the treatment
   *  register, where nothing is ever withheld. */
  needsReconcileCount?: number;
}

/**
 * The tone strip's third cell.
 *
 * Two things are wrong with the version this replaces. The small one: when the
 * count was non-zero the note reported the number of *historical escalations*,
 * a different number about a different thing sitting under the open-items
 * value — a cell describing itself twice, incompatibly.
 *
 * The load-bearing one: on a bereaved family, "N open items" is not the whole
 * truth, because the treatment-era ones among them have already stopped
 * rendering on the family's own screens (#693). Telling a counselor there are
 * three items open "with the family" when the family can see none of them is
 * the same class of lie as the escalation pill. So in the bereaved register
 * the note points at the reconcile panel that is sitting further down the same
 * page waiting for a decision.
 */
export function getOpenItemsStat(
  register: StaffRegister,
  input: OpenItemsStatInput,
): StatCellCopy {
  const { openCount } = input;
  const pending = input.needsReconcileCount ?? 0;

  let note: string;
  if (openCount === 0) {
    note = 'Nothing in flight';
  } else if (register === 'bereaved' && pending > 0) {
    note = `${pending} treatment-era ${pending === 1 ? 'item' : 'items'} to review`;
  } else {
    note = 'Open with the family';
  }

  return { label: 'Open items', value: String(openCount), note };
}

// ── The card chip ─────────────────────────────────────────────────────────

export interface ContactChipInput {
  checkIn?: CheckInLike | null;
  now?: Date;
}

/**
 * The small mono chip beside a family's name. Returns null when there is
 * nothing to say, which is how the caller suppresses the element entirely
 * rather than rendering an empty span.
 *
 * Bereaved register only. The treatment register used to hand in a
 * "Silent · 3 wks" label here; that label read the dead activity clock and
 * retired with it (#1983), so in treatment this chip is always null — the
 * card names the family and says nothing about how long they have been quiet,
 * because nothing measures it.
 *
 * The bereaved chip states an elapsed duration, not a calendar date. It used
 * to print a bare "MMM d", which on a check-in from 2024 rendered
 * "LAST CONTACT · JUN 24" — a counselor reads that as seven weeks ago when the
 * real answer is 26 months, and this chip is the at-a-glance form of the one
 * number this register exists to keep honest. The exact date is still one
 * glance away in the stat cell, which carries the year.
 */
export function getContactChip(
  register: StaffRegister,
  input: ContactChipInput,
): string | null {
  if (register !== 'bereaved') return null;
  const ago = agoPhrase(input.checkIn?.at, input.now ?? new Date());
  return ago ? `Last contact · ${ago}` : 'Last contact · not yet';
}

// ── Attention signals ─────────────────────────────────────────────────────

/**
 * The bereaved register's stand-in for "Steady" / "Silent · 41 days" on the
 * card's signal line. "Steady" is a judgment about engagement and does not
 * apply; this states the operational fact instead.
 */
export const BEREAVED_NO_FLAGS = 'No open flags';

// ── The caseload aggregate ────────────────────────────────────────────────

/**
 * The last branch of the /trfer/families owed line — the sentence that prints
 * when no bucket has anything in it.
 *
 * This is the residue the exclusion left behind, and the only place in this
 * register where the wrong word arrives by *absence* rather than by
 * assertion. Bereaved families now bucket to stable, score 0 on attention and
 * render no pill, so all three earlier branches of that line ("N families are
 * on escalation", "N have gone quiet") correctly skip them — and the
 * fall-through then said "All 6 families are steady right now", sweeping a
 * family whose child died in April into the word "steady". Milder than the
 * pill, because it is an aggregate rather than a claim about one family, and
 * still the exact judgment word every per-family surface in this register
 * removes.
 *
 * So the count is split rather than the word softened: a counselor who can
 * see that two families sit outside the steady claim can trust the claim; one
 * who cannot see it reads a number that quietly includes them, and the
 * caseload's own headline is the last place that should be doing arithmetic
 * the reader can't check.
 *
 * The bereaved clause deliberately reuses the wording of `BEREAVED_NO_FLAGS`
 * — the per-card signal line — so the aggregate and the cards it summarizes
 * say one thing in one phrase. Kept as its own literal rather than
 * interpolating that constant: the two must read alike, but a TRF rewrite of
 * the card's fragment must not be able to ungrammatical this sentence.
 *
 * @param steadyCount   families in the treatment register, all of which are
 *                      steady by the time this branch is reached.
 * @param bereavedCount families held out of the claim.
 */
export function getCaseloadSteadyLine(steadyCount: number, bereavedCount: number): string {
  const steady = `All ${steadyCount} ${steadyCount === 1 ? 'family is' : 'families are'} steady right now`;
  const bereaved = `${bereavedCount} bereaved ${bereavedCount === 1 ? 'family has' : 'families have'} no open flags`;
  if (bereavedCount <= 0) return `${steady}.`;
  if (steadyCount <= 0) return `${bereaved}.`;
  return `${steady}; ${bereaved}.`;
}

// The tone-strip exclusion caption (`getSilenceExclusionNote`, "N bereaved
// families not counted") retired with the silence sentence it captioned
// (#1983): there is no silent count left to hold anyone out of.

// ── The check-in composer ─────────────────────────────────────────────────

/**
 * The pre-written draft, branched on the register.
 *
 * The treatment draft ("…see how things are going… anything we can help with
 * this week?") fails twice for a bereaved family: it assumes a treatment
 * cadence, and it ends in a question that demands a reply from someone who
 * may have no reply to give. What it gets right is naming the child, and that
 * is kept — bereavement practice is unambiguous that families fear the child
 * going unmentioned, and a note that carefully avoids the name is worse than
 * no note.
 *
 * The load-bearing inverse of the treatment draft is the explicit no-reply
 * phrase. It is the sentence that makes the message a gift rather than an
 * obligation, and it is the one part of this copy that should survive any
 * rewrite TRF makes.
 */
export function getCheckInDraft(
  register: StaffRegister,
  childName?: string | null,
): string {
  const name = (childName ?? '').trim();

  if (register === 'bereaved') {
    return name
      ? `Hi, just thinking about you and ${name}. No reply needed — I only wanted you to know we’re still here, and I’m still your contact whenever you want anything.`
      : 'Hi, just thinking about you and your family. No reply needed — I only wanted you to know we’re still here, and I’m still your contact whenever you want anything.';
  }

  return name
    ? `Hi, just thinking about you and ${name}. Wanted to check in and see how things are going. Is there anything we can help with this week?`
    : 'Hi, just thinking about you. Wanted to check in and see how things are going. Is there anything we can help with this week?';
}

/**
 * The italic note under the composer textarea. In the bereaved register it
 * tells the counselor why this draft reads differently, so the difference
 * reads as deliberate rather than as a bug they should "fix" back.
 *
 * The delivery clause is conditional, in both registers, because the flat
 * promise was false: the email half only goes out for a caregiver who has
 * email notifications switched on, and `notifyFamilyParent` sends nothing at
 * all to a family with no linked caregiver account. This is the pre-send half
 * of the same claim the success toast makes afterwards; the two have to agree,
 * and neither may over-promise. The toast, which knows what actually happened,
 * is the one that reports the outcome.
 */
export function getComposerHint(register: StaffRegister): string {
  if (register === 'bereaved') {
    return 'They’ll get this in the app, and by email if their account has email turned on. The draft says no reply is needed — worth keeping, whatever else you change.';
  }
  return 'They’ll get this in the app, and by email if their account has email turned on — short and warm tends to land better than long.';
}

// ── The replacement: remembrance-date callouts ────────────────────────────

/** One row of `GET /trfer/calendar` (types/api :: TrferCalendarEvent). */
export interface RemembranceEventShape {
  family_id: string;
  /** Already suffixed by the backend, e.g. "Ellis family". */
  family_name: string;
  patient_first_name: string;
  type: 'birthday' | 'angel_date';
  days_until: number;
}

/** Structurally a `WilliamsPattern` (lib/trfer-display). Mapped field by
 *  field at the push site so a new field on that interface is a compile
 *  error here rather than a silently missing prop. */
export interface RemembranceCallout {
  id: string;
  body: string;
  ctaLabel: string;
  prompt: string;
}

/** At most this many remembrance callouts at once — the callout is a nudge,
 *  not a worklist, and a stack of them reads as a batch. */
export const MAX_REMEMBRANCE_CALLOUTS = 3;

function whenPhrase(daysUntil: number): string {
  if (daysUntil <= 0) return 'today';
  if (daysUntil === 1) return 'tomorrow';
  return `in ${daysUntil} days`;
}

/**
 * The surface that replaces the silent-batch nudge for bereaved families.
 *
 * Three properties are load-bearing, in this order:
 *
 *  1. **It exists at all.** This is the "replacement, not removal" half of
 *     #685. Excluding bereaved families from the silent count with nothing in
 *     its place means no surface ever suggests contacting them again.
 *  2. **One callout per family, never an aggregate.** No count, no "the 3
 *     families with remembrance dates this month". A remembrance date is not
 *     a queue.
 *  3. **The CTA opens a review, never a draft.** The silent-batch pattern's
 *     prompt asks Williams to *write* messages; this one asks it to bring the
 *     family's record up. Auto-drafted, sendable text is the most dangerous
 *     output the platform can produce on this path, and a human decides
 *     whether anything goes out at all.
 *
 * Callers pass events already scoped to bereaved families — a living
 * patient's birthday is not this register's business.
 */
export function buildRemembranceCallouts(
  events: RemembranceEventShape[] | null | undefined,
): RemembranceCallout[] {
  if (!events?.length) return [];

  return [...events]
    .sort((a, b) => a.days_until - b.days_until)
    .slice(0, MAX_REMEMBRANCE_CALLOUTS)
    .map((event) => {
      const child = event.patient_first_name?.trim() || 'their child';
      const when = whenPhrase(event.days_until);
      const occasion = event.type === 'angel_date' ? 'remembrance date' : 'birthday';
      return {
        id: `remembrance-${event.type}-${event.family_id}`,
        body: `${child}’s ${occasion} is ${when}. Worth deciding now whether a note goes out to the ${event.family_name}, and from whom.`,
        ctaLabel: `Review the ${event.family_name}`,
        prompt: `Bring me up to date on the ${event.family_name} before ${child}’s ${occasion} ${when} — what’s on their record, what we’ve done with them before, and who should reach out.`,
      };
    });
}

// ── Treatment-era open items ──────────────────────────────────────────────
//
// The third named export of this module in the spec, and the one both sides
// of the platform read: the parent surfaces use it to decide what silently
// stops rendering (#693), the staff profile uses it to decide what goes into
// the reconcile prompt (#685 surface 5). One classifier, so the item a
// counselor is asked to reconcile is exactly the item the family stopped
// seeing — a second definition would let the two lists disagree.

/** The reconcile decision a TRFer records on one item. Nullable sub-document
 *  on the task, `bereavement_reconcile`, mirroring `last_check_in`'s shape. */
export interface TaskReconcileShape {
  decision?: 'kept' | 'closed' | null;
  at?: string | null;
  by_user_id?: string | null;
  by_name?: string | null;
}

/** The minimum a task needs to be classified. Structural on purpose: the
 *  parent holds `Task` from types/api, the staff profile holds the raw
 *  `open_tasks` document off `GET /trfer/families/:id`. */
export interface TreatmentEraTaskShape {
  title?: string | null;
  description?: string | null;
  category?: string | null;
  status?: string | null;
  metadata?: {
    origin?: string | null;
    auto_rule_id?: string | null;
    medication_id?: string | null;
    appointment_id?: string | null;
  } | null;
  bereavement_reconcile?: TaskReconcileShape | null;
}

/** The three `cycleAutoTasks` rules. Every task they write is derived from a
 *  medication or an appointment record, so the rule id alone is proof. */
const AUTO_TREATMENT_RULES = new Set(['refill_prep', 'post_chemo_recovery', 'appointment_prep']);

/**
 * Treatment vocabulary, deliberately narrow.
 *
 * These are words for a treatment *activity*, not for a place or an
 * institution. "hospital" and "clinic" are absent on purpose: "Return the
 * wheelchair to the hospital" is equipment paperwork, which the #644 memo
 * names as real open business a bereaved family still has with TRF, and a
 * place-name match would have swallowed it.
 *
 * The list is still a heuristic and will mis-sort some hand-typed titles.
 * That is survivable only because of the staff override below — "Keep item"
 * puts a misclassified task straight back in front of the family. Read the
 * two as one mechanism: the classifier is the default, the human is the
 * decision.
 *
 * TWIN, and the pointer the backend copy asks for in return: this list and
 * `isTreatmentEraTask` / `isHiddenFromBereavedFamily` are duplicated in
 * `backend/src/models/Task.js` (the duplication is argued there — there is no
 * shared build target between Vite-compiled TS, bundler-less Node ESM, and the
 * Python agent that reaches tasks over HTTP). The duplication is asymmetric:
 * **the backend copy is the enforcement point and this one is a render-time
 * convenience** — this filter can only hide what the API already sent, while
 * that one decides what the API sends at all, which is the half that also
 * governs the Clio agent's `get_tasks`. Edit the two vocabulary lists in the
 * same commit; `backend/tests/models/task-treatment-era-classifier.test.js`
 * reads this file as text and fails the backend suite if they drift.
 */
const TREATMENT_TERMS = [
  'prescription', 'refill', 'pharmacy', 'medication', 'medicine', 'meds', 'dose', 'dosage',
  'chemo', 'chemotherapy', 'infusion', 'transfusion', 'radiation', 'immunotherapy', 'steroid',
  'oncology', 'oncologist', 'biopsy', 'bloodwork', 'blood draw', 'lab work', 'labs',
  'mri', 'ct scan', 'pet scan', 'ultrasound', 'x-ray', 'scan',
  'port flush', 'picc', 'central line', 'anti-nausea', 'antinausea',
  'pre-op', 'post-op', 'surgery', 'treatment', 'appointment', 'appt', 'discharge',
];

const TREATMENT_TERM_RE = new RegExp(
  `\\b(${TREATMENT_TERMS.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`,
  'i',
);

/**
 * Is this item a leftover from the treatment era?
 *
 * Three structural signals first, because they cannot be wrong:
 *  1. the task was written by a `cycleAutoTasks` rule,
 *  2. it carries a link to a medication or an appointment record,
 *  3. its category is `hospital_stay`, a category that exists only for care.
 *
 * Then the vocabulary match on title + description, which is what catches the
 * case the issue is actually named after — "Pick up Lila's prescription",
 * hand-typed by a parent, category `errand`, no metadata at all.
 *
 * Note there is no date comparison against the moment of the stage change.
 * The spec phrases the rule as "created before the stage change", and in
 * practice every such item is; but a treatment-era task created *after* the
 * transition is a data-entry slip, and showing a bereaved parent their
 * child's refill because the clock says it is new would be the same failure
 * with a timestamp attached. The classifier keys on what the item is, not on
 * when it was typed.
 */
export function isTreatmentEraTask(task: TreatmentEraTaskShape | null | undefined): boolean {
  if (!task) return false;

  const meta = task.metadata;
  if (meta) {
    if (meta.auto_rule_id && AUTO_TREATMENT_RULES.has(meta.auto_rule_id)) return true;
    if (meta.medication_id || meta.appointment_id) return true;
  }
  if (task.category === 'hospital_stay') return true;

  const text = `${task.title ?? ''} ${task.description ?? ''}`;
  return TREATMENT_TERM_RE.test(text);
}

/** Statuses that mean "still on the family's plate". `archived` is not one of
 *  them — that is the resolved state this whole flow produces. */
const OPEN_TASK_STATUSES = new Set(['open', 'claimed', 'in_progress']);

export function isOpenTask(task: TreatmentEraTaskShape | null | undefined): boolean {
  return !!task && OPEN_TASK_STATUSES.has(task.status ?? '');
}

/**
 * The items a TRFer is asked to reconcile when a family becomes bereaved:
 * open, treatment-era, and not already decided.
 *
 * Deliberately excludes anything already carrying a decision, in either
 * direction. A "kept" item is settled business; a "closed" item is archived
 * and gone. Re-prompting on a decision a human already made is how a prompt
 * becomes noise a counselor learns to dismiss without reading.
 */
export function tasksNeedingReconcile<T extends TreatmentEraTaskShape>(
  tasks: T[] | null | undefined,
): T[] {
  if (!tasks?.length) return [];
  return tasks.filter(
    (t) => isOpenTask(t) && !t.bereavement_reconcile?.decision && isTreatmentEraTask(t),
  );
}

/**
 * Does this item stop rendering on the bereaved family's own surfaces?
 *
 * Only ever consulted in memorial mode by the caller. Three branches, in
 * priority order:
 *
 *  - **A staff "Keep item" wins outright.** This is the escape hatch that
 *    makes the heuristic classifier acceptable: the one human who knows the
 *    family can put any item back, and the parent sees it again immediately.
 *  - **Archived items are gone.** Closed-no-longer-applied is staff history,
 *    never a parent row — showing a tombstone would defeat the point of
 *    closing it silently.
 *  - Otherwise: open treatment-era items are withheld, everything else
 *    renders. Completed and cancelled treatment items stay: they are the
 *    family's own record of what they did, and deleting history is not this
 *    feature's business.
 */
export function isHiddenFromBereavedFamily(
  task: TreatmentEraTaskShape | null | undefined,
): boolean {
  if (!task) return false;
  if (task.bereavement_reconcile?.decision === 'kept') return false;
  if (task.status === 'archived') return true;
  if (!isOpenTask(task)) return false;
  return isTreatmentEraTask(task);
}

// ── The staff-side reconcile copy ─────────────────────────────────────────
//
// Every string in the reconcile flow, same one-file review posture as the
// rest of this module. The two verbs are fixed by design-grammar §4 (verb +
// object, and a confirming button repeats its verb), and the resolution word
// is "Close", never "Complete" and never "Delete": marking a prescription
// pickup complete records that someone picked it up, which did not happen,
// and deleting it destroys the fact that TRF was tracking it at all.

/** The status word an archived item renders with. Neutral form only —
 *  `ink-3` on `bg-3` per grammar §3. Never emerald (it is not done) and
 *  never rose (nothing went wrong). */
export const RECONCILE_CLOSED_LABEL = 'Closed — no longer applied';

export const RECONCILE_KEEP_LABEL = 'Keep item';
export const RECONCILE_CLOSE_LABEL = 'Close item';

export interface ReconcilePromptCopy {
  eyebrow: string;
  title: string;
  body: string;
}

/**
 * The prompt itself, at the stage-change moment and every visit after until
 * it is resolved.
 *
 * The header names the decision, not a count (desk pages 2026-09-19, S12 —
 * the rows beneath are the count, guideline §1.6); the body still says how
 * many, in a sentence. No recommendation about which way to decide, because
 * the platform does not know: an equipment return and a prescription pickup
 * look identical to the classifier and completely different to the counselor.
 * And no bulk control anywhere in this flow — a single "Close all" would let
 * one click both destroy a real obligation and write a false record of why
 * it went away.
 */
export const RECONCILE_PROMPT_TITLE = 'Items from before — keep or close';

export function getReconcilePromptCopy(itemCount: number): ReconcilePromptCopy {
  const noun = itemCount === 1 ? 'item' : 'items';
  return {
    eyebrow: 'Open items from treatment',
    title: RECONCILE_PROMPT_TITLE,
    body:
      `The family stage changed to bereaved with ${itemCount} open treatment-era ${noun} on record. ` +
      `They have already stopped showing on the family’s own screens. ` +
      `Decide each one: keep it if it is still real work, close it if it no longer applies.`,
  };
}

/** The line under the per-item buttons, explaining what each verb does to the
 *  record. Staff-facing, so it is operational, not reassuring. */
export const RECONCILE_ROW_HINT =
  'Keeping an item puts it back on the family’s list. Closing it files it as no longer applied — it stays in this record and is never marked done.';
